import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth, useUser } from "@clerk/clerk-react";
import { supabase } from "@/integrations/supabase/client";
import { applyConsent, getSignalPayload, onConsentUIRequest, retryPendingRegistration, trackPageView } from "@/lib/metaPixel";

/** PageViews on route change, signup conversion, and server signal sync. */
export function useMetaPixel() {
  const location = useLocation();
  const { isSignedIn, user } = useUser();
  const { getToken } = useAuth();
  const synced = useRef<string | null>(null);

  useEffect(() => { void applyConsent(); }, []);
  useEffect(() => { void trackPageView(); }, [location.pathname]);

  // CompleteRegistration fires at onboarding save; here we only retry a pending
  // one if the user grants consent shortly after onboarding.
  useEffect(() => {
    if (!isSignedIn || !user?.id) return;
    const id = user.id;
    void retryPendingRegistration(id);
    const off = onConsentUIRequest(() => void retryPendingRegistration(id));
    return () => { off(); };
  }, [isSignedIn, user?.id]);

  // Sync browser IDs + consent to the server (for Purchase events).
  useEffect(() => {
    if (!isSignedIn || !user?.id) return;
    const sync = async () => {
      try {
        const payload = await getSignalPayload();
        const sig = user.id + JSON.stringify(payload);
        if (synced.current === sig) return;
        synced.current = sig;
        const token = await getToken();
        await supabase.functions.invoke("meta-signal", {
          body: payload,
          ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
        });
      } catch { /* non-critical */ }
    };
    const t = setTimeout(sync, 3000);
    const off = onConsentUIRequest(() => void sync());
    return () => { clearTimeout(t); off(); };
  }, [isSignedIn, user?.id, getToken]);
}
